import type { CrowdexAssistant } from '@crowdex/agent-contracts/api';
import { cn } from '@crowdex/ui/lib/utils';
import { SlidersHorizontal } from 'lucide-react';
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useProjectMenu } from '@/components/desktop-shell/conversation-menu';
import { useAvatars } from '@/features/workspaces/workspace-resources';
import { openAssistant, useWorkspaceDirectory } from '@/lib/assistants/directory';
import { openWorkspaceDialog } from '@/lib/assistants/workspace-dialogs';
import { movePinnedConversation, usePinnedConversations } from '@/lib/conversation-pins';
import {
  buildSidebar,
  SHOW_OPTIONS,
  type SidebarPinnedEntry,
  type SidebarRow,
} from '@/lib/desktop-shell/sidebar-model';
import { openShellSearch } from './search-dialog';
import { useShellNavigation, useSidebarWaiting } from './shell-navigation';
import { useSidebarView } from './sidebar-preferences';
import { AssistantRow, ConversationRow, ProjectDot, SectionHeading, StepRow } from './sidebar-rows';
import { SidebarFilterChip, SidebarViewMenu } from './view-menu';

type Density = 'desktop' | 'drawer';

/**
 * The body of the sidebar and of the phone's drawer (D1, mobile 2, docs/adr-assistants.md): the
 * assistants, the home one first, each its channel's row (the current one unfolded on its work),
 * then Needs you, what the user pinned (D22) and the conversations outside any assistant, as its
 * View groups and filters them (its button at the right of the Conversations heading).
 */
export function WorkspaceBody({
  density = 'desktop',
  onOpenView,
  onLongPress,
  wrapRow = (_row, node) => node,
  archivedCount = null,
}: {
  density?: Density;
  /** A phone opens the View as a sheet (2c). */
  onOpenView?: () => void;
  onLongPress?: (row: SidebarRow) => void;
  /** A phone's rows swipe (read, archive). */
  wrapRow?: (row: SidebarRow, node: ReactNode) => ReactNode;
  archivedCount?: number | null;
}) {
  const navigation = useShellNavigation();
  const projects = useWorkspaceDirectory((state) => state.projects);
  const placements = useWorkspaceDirectory((state) => state.placements);
  const { view, setView } = useSidebarView();
  const avatars = useAvatars().data;
  const avatarName = (id: string | null | undefined) =>
    id == null ? null : (avatars.find((avatar) => avatar.id === id)?.name ?? null);
  const waiting = useSidebarWaiting();
  const pinnedIds = usePinnedConversations();
  const [toggledAssistants, setToggledAssistants] = useState<ReadonlySet<string>>(new Set());
  const [toggledParents, setToggledParents] = useState<ReadonlySet<string>>(new Set());
  const model = useMemo(
    () =>
      buildSidebar({
        threads: navigation.threads,
        assistants: projects,
        placements,
        view,
        waiting,
        activeThreadId: navigation.activeThreadId,
        toggledAssistants,
        toggledParents,
        pinnedIds,
      }),
    [
      navigation.threads,
      navigation.activeThreadId,
      projects,
      placements,
      view,
      waiting,
      toggledAssistants,
      toggledParents,
      pinnedIds,
    ],
  );
  const toggled = (id: string) => (current: ReadonlySet<string>) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const toggleProject = (id: string) => setToggledAssistants(toggled(id));
  const chip = SHOW_OPTIONS.find((option) => option.value === view.show && option.value !== 'all');
  // A parent unfolds on its child conversations (D49), one level of indent at most per level; a
  // task on its steps, compact.
  const children = (entry: SidebarRow, depth: 1 | 2) =>
    entry.expanded === true &&
    entry.children !== undefined &&
    entry.children.length > 0 &&
    (entry.task === undefined ? (
      <ul aria-label={`Conversations under ${entry.thread.title}`}>
        {entry.children.map((child) => row(child, depth, entry.thread.title))}
      </ul>
    ) : (
      <ul aria-label={`Steps of ${entry.thread.title}`}>
        {entry.children.map((child) => (
          <li key={child.thread.id}>
            <StepRow density={density} depth={depth} parentTitle={entry.thread.title} row={child} />
          </li>
        ))}
      </ul>
    ));
  const row = (entry: SidebarRow, depth: 0 | 1 | 2 = 0, parentTitle?: string): ReactNode => (
    <li key={entry.thread.id}>
      {wrapRow(
        entry,
        <ConversationRow
          density={density}
          depth={depth}
          onLongPress={onLongPress}
          onToggleChildren={() => setToggledParents(toggled(entry.thread.id))}
          {...(parentTitle === undefined ? {} : { parentTitle })}
          row={entry}
        />,
      )}
      {children(entry, depth === 0 ? 1 : 2)}
    </li>
  );
  const empty =
    model.pinned.length === 0 &&
    model.conversations.length === 0 &&
    model.repositories.length === 0 &&
    model.list.length === 0;
  // The View's button at the right of the Conversations heading; a phone opens it as a sheet.
  const viewButton =
    onOpenView === undefined ? (
      <SidebarViewMenu
        archivedCount={archivedCount}
        onAddRepository={navigation.addRepository}
        onChange={setView}
        onShowArchived={() => openShellSearch({ filter: 'archived' })}
        trigger={
          <button
            aria-label="View"
            className="-my-1 flex size-6 shrink-0 items-center justify-center rounded-control text-subtle-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-control data-[state=open]:text-foreground"
            title="View"
            type="button"
          >
            <SlidersHorizontal className="size-3.5" />
          </button>
        }
        view={view}
      />
    ) : (
      <button
        aria-label="View"
        className="-mr-2 flex size-11 shrink-0 items-center justify-center rounded-control text-muted-foreground active:bg-accent/60"
        onClick={onOpenView}
        type="button"
      >
        <SlidersHorizontal className="size-5" />
      </button>
    );
  const conversationsHeading = (
    <>
      <SectionHeading density={density} label="Conversations" trailing={viewButton} />
      {chip !== undefined && (
        <div className="px-2.5 pt-1 pb-1">
          <SidebarFilterChip label={chip.chip} onClear={() => setView({ show: 'all' })} />
        </div>
      )}
    </>
  );
  return (
    <div className={cn('flex flex-col', density === 'desktop' ? 'gap-0.5' : 'gap-1')}>
      <section aria-label="Assistants">
        <SectionHeading
          density={density}
          label="Assistants"
          trailing={
            <button
              className={cn(
                'hover:text-foreground',
                // A finger's target on a phone (44 px).
                density === 'drawer' && '-mr-2 flex min-h-11 items-center px-2',
              )}
              onClick={() => openWorkspaceDialog({ kind: 'new-project' })}
              type="button"
            >
              + New
            </button>
          }
        />
        <ul>
          {model.assistants.map((entry) => (
            <li key={entry.assistant.id}>
              <ProjectRowWithMenu
                avatar={avatarName(entry.assistant.avatarId)}
                busy={entry.state === 'running'}
                unread={entry.unread}
                active={
                  entry.assistant.conversationId !== null &&
                  entry.assistant.conversationId === navigation.activeThreadId
                }
                density={density}
                expanded={entry.expanded}
                onOpen={() => void openAssistant(entry.assistant, navigation.openConversation)}
                onToggle={() => toggleProject(entry.assistant.id)}
                project={entry.assistant}
                running={entry.running}
                waiting={entry.waiting}
              />
              {entry.expanded && entry.children.length > 0 && (
                <ul aria-label={`Conversations of ${entry.assistant.name}`}>
                  {entry.children.map((child) => row(child, 1))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </section>

      {model.needsYou.length > 0 && (
        <section aria-label="Needs you">
          <SectionHeading density={density} label="Needs you" />
          <ul>
            {model.needsYou.map((entry) => (
              <li key={entry.thread.id}>
                {wrapRow(
                  entry,
                  <ConversationRow
                    bold
                    density={density}
                    onLongPress={onLongPress}
                    row={entry}
                    // Its assistant's colour square, as on the assistant's row (a round dot
                    // is what waits on you).
                    trailing={
                      entry.assistantColor === null ? undefined : (
                        <ProjectDot className="size-2" color={entry.assistantColor} />
                      )
                    }
                  />,
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {model.pinned.length > 0 && (
        <PinnedSection
          density={density}
          entries={model.pinned}
          onLongPress={onLongPress}
          onToggleChildren={(id) => setToggledParents(toggled(id))}
          renderChildren={(entry) => children(entry, 1)}
          wrapRow={wrapRow}
        />
      )}

      {view.groupBy === 'assistant' && (
        <section aria-label="Conversations">
          {conversationsHeading}
          <ul>{model.conversations.map((entry) => row(entry))}</ul>
        </section>
      )}

      {view.groupBy === 'repository' && (
        <>
          <div>{conversationsHeading}</div>
          {model.repositories.map((group) => (
            <section aria-label={group.repository} key={group.repository}>
              <SectionHeading density={density} label={group.repository} />
              <ul>{group.rows.map((entry) => row(entry))}</ul>
            </section>
          ))}
        </>
      )}

      {view.groupBy === 'none' && (
        <section aria-label="Conversations">
          {conversationsHeading}
          <ul>{model.list.map((entry) => row(entry))}</ul>
        </section>
      )}

      {empty && view.groupBy !== 'assistant' && (
        <p className="type-caption px-2.5 py-2 text-muted-foreground">
          {view.show === 'all' ? 'No conversations yet.' : 'No conversation matches this view.'}
        </p>
      )}
    </div>
  );
}

/** Where a dragged pinned conversation would land: before or after the row under the pointer. */
type PinnedDrop = { id: string; side: 'before' | 'after' };

/**
 * Pinned (D22): the conversations the user pinned, above the projects, in their pinned order (the
 * latest first). A mouse drags them into another order, ⌥↑ / ⌥↓ move the focused one; Unpin (the
 * row's menu) puts it back in its place. A project's shows its project's name, dimmed.
 */
function PinnedSection({
  entries,
  density,
  onLongPress,
  onToggleChildren,
  renderChildren,
  wrapRow,
}: {
  entries: readonly SidebarPinnedEntry[];
  density: Density;
  onLongPress: ((row: SidebarRow) => void) | undefined;
  onToggleChildren: (id: string) => void;
  renderChildren: (entry: SidebarPinnedEntry) => ReactNode;
  wrapRow: (row: SidebarRow, node: ReactNode) => ReactNode;
}) {
  const ids = entries.map((entry) => entry.thread.id);
  const { drop, onPointerDown } = usePinnedDrag(ids);
  const move = (id: string, delta: -1 | 1) => {
    const index = ids.indexOf(id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    movePinnedConversation(id, delta < 0 ? (ids[target] ?? null) : (ids[target + 1] ?? null));
  };
  return (
    <section aria-label="Pinned">
      <SectionHeading density={density} label="Pinned" />
      <ul onPointerDownCapture={onPointerDown}>
        {entries.map((entry) => (
          <li
            className={cn(
              'relative',
              drop?.id === entry.thread.id &&
                "before:absolute before:inset-x-2.5 before:z-10 before:h-0.5 before:rounded-full before:bg-running before:content-['']",
              drop?.id === entry.thread.id && drop.side === 'before' && 'before:-top-px',
              drop?.id === entry.thread.id && drop.side === 'after' && 'before:-bottom-px',
            )}
            data-pinned-item={entry.thread.id}
            key={entry.thread.id}
          >
            {wrapRow(
              entry,
              <ConversationRow
                density={density}
                onLongPress={onLongPress}
                onMovePinned={(delta) => move(entry.thread.id, delta)}
                onToggleChildren={() => onToggleChildren(entry.thread.id)}
                pinned
                row={entry}
                trailing={
                  entry.assistantName === null ? undefined : (
                    <span className="type-caption max-w-[40%] shrink-0 truncate text-subtle-foreground">
                      {entry.assistantName}
                    </span>
                  )
                }
              />,
            )}
            {renderChildren(entry)}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Dragging a pinned conversation with the mouse among the others (the row's own drag, which
 * splits or moves to a project, finds no target in the sidebar): a line shows where it lands.
 */
function usePinnedDrag(ids: readonly string[]) {
  const [drop, setDrop] = useState<PinnedDrop | null>(null);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const cleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanup.current?.(), []);

  const onPointerDown = (event: ReactPointerEvent<HTMLUListElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const list = event.currentTarget;
    const dragged = (event.target as HTMLElement)
      .closest<HTMLElement>('[data-pinned-item]')
      ?.getAttribute('data-pinned-item');
    if (dragged === null || dragged === undefined) return;
    const pointerId = event.pointerId;
    const origin = { x: event.clientX, y: event.clientY };
    let dragging = false;
    let current: PinnedDrop | null = null;
    const show = (next: PinnedDrop | null) => {
      current = next;
      setDrop(next);
    };
    const move = (pointer: PointerEvent) => {
      if (pointer.pointerId !== pointerId) return;
      if (!dragging && Math.hypot(pointer.clientX - origin.x, pointer.clientY - origin.y) < 6)
        return;
      dragging = true;
      const item = document
        .elementFromPoint(pointer.clientX, pointer.clientY)
        ?.closest<HTMLElement>('[data-pinned-item]');
      const id = item?.getAttribute('data-pinned-item');
      if (item === null || item === undefined || !list.contains(item) || id == null) {
        show(null);
        return;
      }
      const bounds = item.getBoundingClientRect();
      const side = pointer.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after';
      show(id === dragged ? null : { id, side });
    };
    const finish = (pointer: PointerEvent) => {
      if (pointer.pointerId !== pointerId) return;
      stop();
      const target = current;
      show(null);
      if (!dragging || target === null) return;
      const order = idsRef.current.filter((id) => id !== dragged);
      const at = order.indexOf(target.id);
      const beforeId = target.side === 'before' ? target.id : (order[at + 1] ?? null);
      movePinnedConversation(dragged, beforeId);
    };
    const stop = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      cleanup.current = null;
    };
    cleanup.current?.();
    cleanup.current = stop;
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
  };
  return { drop, onPointerDown };
}

/** An assistant's row, with the menu of its channel's header (H8). */
function ProjectRowWithMenu({
  project,
  ...row
}: { project: CrowdexAssistant } & Omit<
  Parameters<typeof AssistantRow>[0],
  'name' | 'color' | 'menu'
>) {
  const menu = useProjectMenu(project, project.conversationId);
  return (
    <>
      <AssistantRow {...row} color={project.color} menu={menu.entries} name={project.name} />
      {menu.dialogs}
    </>
  );
}
